// src/client/renderer/character/cape-shader-params.ts
//
// Per-cape wind-sway shader parameter registry (Plan 13, Task 3.2).
//
// Registry-as-data: maps each non-none CapeId to { intensity, speed, stiffness }
// plus optional FLAG-ONLY metadata fields (emit, colorShift).
//
// FLAG-ONLY metadata:
//   - emit ('embers' | 'glow') is a Plan 14b particle-system concern — declared here,
//     no shader effect shipped in Plan 13.
//   - colorShift is likewise deferred — declared here for consumer visibility,
//     no shader effect shipped in Plan 13.
//
// Consumer: applyCapeParams (this file) is called by attachCape (cape.ts Task 3.1)
// immediately after the fresh cape mesh is created (NOT on texture-swap — params
// depend on capeId, which doesn't change during a texture swap).

import * as THREE from 'three';
import type { CapeId } from './types';

/** Per-cape wind-sway parameter overrides per spec §9 lines 670-678. Registry-as-data;
 *  no implementation. Consumer (cape.ts attachCape from Task 3.1) reads these and writes
 *  to the ShaderMaterial.uniforms uIntensity/uSpeed/uStiffness slots produced by
 *  createCapeShader (Task 3.1).
 *
 *  emit + colorShift fields are FLAG-ONLY metadata (consumer-visible) — no shader effect
 *  in Plan 13. emit ('embers'/'glow') is a Plan 14b consumer concern (particle system).
 *  colorShift is also deferred (no consumer until Plan 14b/16). */
export const CAPE_SHADER_PARAMS: Record<
  Exclude<CapeId, 'none'>,
  {
    intensity: number;
    speed: number;
    stiffness: number;
    emit?: 'embers' | 'glow';
    colorShift?: boolean;
  }
> = {
  starter: { intensity: 0.1, speed: 1.5, stiffness: 1.5 }, // gentle
  fire: { intensity: 0.18, speed: 2.6, stiffness: 1.0, emit: 'embers' }, // aggressive + ember flag
  rainbow: { intensity: 0.1, speed: 1.5, stiffness: 1.5, colorShift: true }, // normal + color-shift
  cosmic: { intensity: 0.08, speed: 0.7, stiffness: 1.8 }, // slow ethereal
  golden: { intensity: 0.06, speed: 1.0, stiffness: 2.6 }, // heavy
  wireframe: { intensity: 0.05, speed: 1.4, stiffness: 3.0 }, // stiff tech
  water: { intensity: 0.14, speed: 1.8, stiffness: 0.9 }, // fluid wave
  lava: { intensity: 0.07, speed: 0.8, stiffness: 2.4, emit: 'glow' }, // slow heavy + glow
};

/**
 * Pure function: apply CAPE_SHADER_PARAMS[capeId] to the cape mesh's ShaderMaterial uniforms.
 *
 * Reads the three numeric fields (intensity, speed, stiffness) and writes them to
 * mesh.material.uniforms.uIntensity/uSpeed/uStiffness.
 *
 * Called ONLY on the "new mesh" path in attachCape — NOT on the texture-swap path
 * because params are keyed on capeId, which is unchanged during a texture swap.
 */
export function applyCapeParams(capeMesh: THREE.Mesh, capeId: Exclude<CapeId, 'none'>): void {
  const params = CAPE_SHADER_PARAMS[capeId];
  const mat = capeMesh.material as THREE.ShaderMaterial;
  mat.uniforms['uIntensity'].value = params.intensity;
  mat.uniforms['uSpeed'].value = params.speed;
  mat.uniforms['uStiffness'].value = params.stiffness;
}
